/**
 * PublicVerifyPage — `/v/:id`. No auth, no shell, no login.
 *
 * Lives outside `ProtectedRoute` so a phone-camera QR scan can land
 * directly on this URL and verify a signed GI without any account.
 *
 * Two id formats are accepted, dispatched on shape:
 *
 *  - **UUID** — a real engine `root_id`. Two API calls, both hit the
 *    public-GET allow-list on the server (`auth.rs::UNAUTH_GET_PATHS`):
 *      GET /api/v1/gi/{id}         → name, domain, author, hash
 *      GET /api/v1/gi/{id}/verify  → 204 = ✅ valid; 422 = ❌ invalid
 *    Bare `fetch` (NOT `apiFetch`) — `apiFetch` injects bearer +
 *    signing headers, which a logged-out visitor doesn't have. The
 *    endpoints are on the unauth list so plain GETs work.
 *
 *  - **64-char hex** — a SHA-256, e.g. from the `/demo` page's
 *    browser-side signing flow. The server has no record of it (and
 *    looking it up would 404), so we render a "Demo signature" card
 *    with a clear "not stored on server" disclaimer. The shape itself
 *    is enough to confirm "this URL came from a GI signing flow"
 *    without misleading the viewer about the verification depth.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { apiBaseUrl } from '@/api/http';
import type { Manifest, SignedEntity, BehavioralContract } from '@/api/types';

const ACCENT = '#6366f1';

interface EntityRecordWire {
  manifest: Manifest;
  signed_entity: SignedEntity;
  contract: BehavioralContract;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error'; detail: string }
  | { kind: 'ok'; record: EntityRecordWire; verified: boolean | null }
  | { kind: 'demo'; hash: string };

export function PublicVerifyPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    // Demo flow first: 64-char hex sha256 from `/demo`'s browser
    // signing. No server round-trip — the API has no record of these.
    if (isPlausibleSha256(id)) {
      setState({ kind: 'demo', hash: id.toLowerCase() });
      return () => {
        cancelled = true;
      };
    }

    if (!isPlausibleUuid(id)) {
      setState({ kind: 'error', detail: 'Invalid id format.' });
      return () => {
        cancelled = true;
      };
    }
    const base = apiBaseUrl();
    const recordUrl = `${base}/gi/${encodeURIComponent(id)}`;
    const verifyUrl = `${base}/gi/${encodeURIComponent(id)}/verify`;

    // Load the record first — it's fast and is what the visitor came to see.
    // The signature check (`/verify`) re-verifies the hash chain and can be
    // slow on a large log, so it runs SEPARATELY with a hard timeout: a hung
    // verify must never leave the page stuck on the spinner. The record shows
    // immediately; the verdict fills in when it resolves, or degrades to
    // "check unavailable" on timeout — never an infinite spin.
    fetch(recordUrl, { headers: { Accept: 'application/json' } })
      .then(async (recordRes) => {
        if (cancelled) return;
        if (recordRes.status === 404) {
          setState({ kind: 'not-found' });
          return;
        }
        if (!recordRes.ok) {
          setState({
            kind: 'error',
            detail: `Could not load record (HTTP ${recordRes.status}).`,
          });
          return;
        }
        const record = (await recordRes.json()) as EntityRecordWire;
        if (cancelled) return;
        // Render the record right away; signature verdict still pending.
        setState({ kind: 'ok', record, verified: null });

        // Signature check with a 12s timeout so a slow/hung endpoint can't
        // block the page. Success (204) ⇒ valid, 422 ⇒ invalid, anything
        // else (timeout, network) ⇒ leave the verdict unknown.
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12_000);
        try {
          const verifyRes = await fetch(verifyUrl, {
            headers: { Accept: 'application/json' },
            signal: ctrl.signal,
          });
          clearTimeout(timer);
          if (cancelled) return;
          setState({ kind: 'ok', record, verified: verifyRes.status === 204 });
        } catch {
          clearTimeout(timer);
          if (cancelled) return;
          setState({ kind: 'ok', record, verified: null });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          detail: e instanceof Error ? e.message : String(e),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <main className="mx-auto flex min-h-screen max-w-md flex-col px-5 pt-10 pb-8">
        {/* Brand mark — small, top centre */}
        <div className="flex flex-col items-center gap-2" style={{ color: ACCENT }}>
          <GiOrbitLogo size="md" decorative />
          <p className="text-[11px] font-semibold uppercase tracking-wide">
            Verified by GI Engine
          </p>
        </div>

        <div className="mt-8 flex-1">
          {state.kind === 'loading' ? (
            <Loading />
          ) : state.kind === 'not-found' ? (
            <NotFound id={id} />
          ) : state.kind === 'error' ? (
            <ErrorBox detail={state.detail} />
          ) : state.kind === 'demo' ? (
            <DemoResult hash={state.hash} />
          ) : (
            <Result record={state.record} verified={state.verified} />
          )}
        </div>

        <Footer />
      </main>
    </div>
  );
}

// ─── Sub-views ─────────────────────────────────────────────────────

function Loading() {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-zinc-500">
      <div
        className="h-9 w-9 animate-spin rounded-full border-2 border-zinc-200"
        style={{ borderTopColor: ACCENT }}
        aria-hidden
      />
      <p>Verifying signature…</p>
    </div>
  );
}

function NotFound({ id }: { id: string }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 text-center">
      <h1 className="text-lg font-semibold">Not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        No signed entity matches this id.
      </p>
      <p className="mt-3 break-all font-mono text-xs text-zinc-400">{id}</p>
    </section>
  );
}

function ErrorBox({ detail }: { detail: string }) {
  return (
    <section className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 text-center">
      <h1 className="text-lg font-semibold">Could not verify</h1>
      <p className="mt-2 text-sm text-zinc-600">{detail}</p>
    </section>
  );
}

function Result({ record, verified }: { record: EntityRecordWire; verified: boolean | null }) {
  const m = record.manifest;
  const domainLabel = typeof m.domain === 'string' ? m.domain : m.domain.Custom;
  const authorPubKey = stripTagged(m.author.public_key);
  // SHA-256 the signature actually covers = the entity manifest hash. NOT
  // wasm_hash, which is all-zeros for non-WASM entities (e.g. a form registration).
  const contentHash = stripTagged(record.signed_entity.manifest_hash);
  const created = formatTimestamp(m.created_at?.iso);

  // Three verdicts: valid (204), invalid (422), or pending/unavailable —
  // the record loaded but the signature check timed out or was unreachable.
  const verdict =
    verified === true
      ? {
          bg: '#eef2ff',
          border: '#c7d2fe',
          icon: ACCENT,
          glyph: '✓',
          title: 'Signature valid',
          note: 'This entity has not been altered since it was signed.',
        }
      : verified === false
        ? {
            bg: '#fef2f2',
            border: '#fecaca',
            icon: '#dc2626',
            glyph: '✗',
            title: 'Signature invalid',
            note: 'The on-chain signature does not match the stored manifest.',
          }
        : {
            bg: '#fffbeb',
            border: '#fde68a',
            icon: '#d97706',
            glyph: '•',
            title: 'Registration found',
            note: 'The signed record loaded successfully. The signature check is taking longer than usual — reload to try again.',
          };

  return (
    <div className="flex flex-col gap-6">
      {/* Headline verdict */}
      <div
        className="rounded-2xl p-6 text-center"
        style={{ backgroundColor: verdict.bg, border: `1px solid ${verdict.border}` }}
      >
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-3xl"
          style={{ backgroundColor: verdict.icon, color: 'white' }}
          aria-hidden
        >
          {verdict.glyph}
        </div>
        <p className="mt-4 text-2xl font-bold tracking-tight">{verdict.title}</p>
        <p className="mt-1 text-sm text-zinc-600">{verdict.note}</p>
      </div>

      {/* Document card */}
      <section className="rounded-2xl border border-zinc-200 p-5">
        <h2 className="text-lg font-semibold leading-tight">{m.name}</h2>
        <div className="mt-2 inline-flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
            style={{ backgroundColor: '#eef2ff', color: ACCENT }}
          >
            {domainLabel}
          </span>
          <span className="text-xs text-zinc-500">v{m.version}</span>
        </div>

        <dl className="mt-5 space-y-3 text-sm">
          <Row label="Author">{m.author.name || authorPubKey.slice(0, 12) + '…'}</Row>
          <Row label="Signed at">{created}</Row>
          <Row label="Signer key">
            <span className="break-all font-mono text-[11px] text-zinc-600">
              {truncate(authorPubKey, 24)}
            </span>
          </Row>
          <Row label="SHA-256">
            <span className="break-all font-mono text-[11px] text-zinc-600">
              {truncate(contentHash, 32)}
            </span>
          </Row>
        </dl>
      </section>
    </div>
  );
}

function DemoResult({ hash }: { hash: string }) {
  // The server has no record of this hash — `/demo` signed it
  // entirely in the browser with the public DEMO key. We render a
  // positive-but-disclaimed card: shape is well-formed (so it really
  // came from a signing flow), but the verification depth is local.
  return (
    <div className="flex flex-col gap-6">
      <div
        className="rounded-2xl p-6 text-center"
        style={{ backgroundColor: '#eef2ff', border: '1px solid #c7d2fe' }}
      >
        <div
          className="mx-auto flex h-14 w-14 items-center justify-center rounded-full text-3xl"
          style={{ backgroundColor: ACCENT, color: 'white' }}
          aria-hidden
        >
          ✓
        </div>
        <p className="mt-4 text-2xl font-bold tracking-tight">Demo signature</p>
        <p className="mt-1 text-sm text-zinc-600">
          The hash is well-formed and was produced by a GI signing flow.
        </p>
        <div
          className="mx-auto mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
          style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
        >
          <span aria-hidden>ⓘ</span>
          Local demo verification — not stored on server
        </div>
      </div>

      <section className="rounded-2xl border border-zinc-200 p-5">
        <h2 className="text-lg font-semibold leading-tight">Signed payload</h2>
        <p className="mt-1 text-sm text-zinc-600">
          From the browser-side demo. To produce a server-anchored
          proof, sign through the <a
            href="/spark"
            className="font-medium hover:underline"
            style={{ color: ACCENT }}
          >Spark</a> wizard instead.
        </p>
        <dl className="mt-5 space-y-3 text-sm">
          <Row label="SHA-256">
            <span className="break-all font-mono text-[11px] text-zinc-600">
              {truncate(hash, 32)}
            </span>
          </Row>
        </dl>
      </section>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
        {label}
      </dt>
      <dd className="text-zinc-800 sm:text-end">{children}</dd>
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-10 text-center text-xs text-zinc-400">
      <a
        href="https://growing-intelligence.com"
        className="font-medium hover:underline"
        style={{ color: ACCENT }}
      >
        growing-intelligence.com
      </a>
    </footer>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

function isPlausibleUuid(id: string): boolean {
  // 8-4-4-4-12 hex, case-insensitive. Cheap pre-check before hitting
  // the backend; the server makes the authoritative parse.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

function isPlausibleSha256(id: string): boolean {
  // 64-char hex, case-insensitive. Matches `/demo`'s signed-payload
  // hash exactly; nothing else in the app emits 64-hex into a URL.
  return /^[0-9a-f]{64}$/i.test(id);
}

function stripTagged(value: string): string {
  // Server emits hash + key fields prefixed with their algorithm tag
  // (`sha256:...`, `ed25519:...`). Strip the tag for display.
  const idx = value.indexOf(':');
  return idx === -1 ? value : value.slice(idx + 1);
}

function truncate(s: string, head: number): string {
  if (s.length <= head + 6) return s;
  return `${s.slice(0, head)}…${s.slice(-6)}`;
}

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}
